import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { MasterRef, QuoteStage } from '@/lib/types';
import type { ComboboxOption } from '@/components/ui';

/**
 * The quote stage list.
 *
 * Fetched WITHOUT the `isActive` filter on purpose. Filtering server-side gives
 * a clean create dropdown but drops the stage a quote already holds once that
 * stage is retired — which reads as "no stage" and would clear the field on the
 * next save. Everything is fetched once and the create/edit split is made at
 * render time by `selectableStages` below.
 *
 * `perPage: 200` is this endpoint's ceiling, and its default of 50 is not the
 * whole table.
 *
 * Unlike `/quotes`, this route requires a bearer token *today*, so an expired
 * session leaves the stage dropdown as the only broken part of the screen.
 * `failed` is surfaced rather than swallowed so a caller can say so.
 */
export function useQuoteStageOptions(enabled = true) {
  const [stages, setStages] = useState<QuoteStage[]>([]);
  // Starts true when enabled so the first paint shows a loading picker rather
  // than an empty one, and nothing calls setState synchronously in an effect.
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listQuoteStages({ page: 1, perPage: 200 }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setStages(result.data);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setStages([]);
        setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { stages, loading, failed };
}

/**
 * The stages a dropdown may offer, given what the quote currently holds.
 *
 * A retired stage is hidden from new selections but kept — and marked — when it
 * is the record's own value. The server checks that a `stageId` exists but
 * deliberately does NOT check `isActive`, so a retired stage is still savable;
 * the only thing that must not happen is silently losing it.
 *
 * The list arrives in curated `sortOrder ASC, name ASC` order. Do not re-sort,
 * and do not hard-code the names: only `Draft` and `Accepted` came from Zoho,
 * and the other three are convention that an admin may rename.
 */
export function selectableStages(
  stages: QuoteStage[],
  current: MasterRef | null,
): ComboboxOption[] {
  const options = stages
    .filter((stage) => stage.isActive || stage.id === current?.id)
    .map((stage) => ({
      value: String(stage.id),
      label: stage.isActive ? stage.name : `${stage.name} (retired)`,
    }));

  // An empty list means "still loading", not "every option was retired", so the
  // stored value is only pinned back once there is a list to be missing from.
  if (current && options.length > 0 && !options.some((o) => o.value === String(current.id))) {
    options.unshift({ value: String(current.id), label: `${current.name} (no longer offered)` });
  }
  return options;
}
